import React from "react";

import { Sorter } from "@impero-com/ui";
import {
    Column,
    RowData,
    Table as TSTableType,
    flexRender,
    Row as ReactTableRow,
    SortDirection,
} from "@tanstack/react-table";
import { cva } from "class-variance-authority";

import { EmptyTableBox } from "./emptyTableBox";
import * as theme from "./table.css";

interface TableProps<T extends RowData> {
    table: TSTableType<T>;
    noDataFallback: NoDataFallback;

    /**
     * When specified the entire row acts like a clickable link.
     */
    buildRowLinkUrl?: (row: ReactTableRow<T>) => string;
}

/**
 * Text and SVG icon shown when the table has no data.
 */
export interface NoDataFallback {
    text: string;
    svgIcon: string;
}

export function Table<T extends RowData>({
    table,
    noDataFallback,
    buildRowLinkUrl,
}: TableProps<T>) {
    const hasRows = table.getRowModel().rows.length > 0;

    if (!hasRows) {
        const { text, svgIcon } = noDataFallback;
        return <EmptyTableBox text={text} svgIcon={svgIcon} />;
    }
    return (
        <table className={theme.table}>
            <Head table={table} />
            <Rows table={table} buildRowLinkUrl={buildRowLinkUrl} />
        </table>
    );
}

interface HeadProps<T> {
    table: TSTableType<T>;
}

/**
 * Columns whose header component handles its own sorting UI set
 * `meta.hasCustomSortingLogic`, so the head cell must not render the shared
 * sorter next to it.
 */
interface ColumnWithCustomSortingLogic {
    columnDef: {
        meta?: {
            hasCustomSortingLogic?: boolean;
        };
    };
}

function Head<T>({ table }: HeadProps<T>) {
    return (
        <thead>
            {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={theme.tableHeadRow}>
                    {headerGroup.headers.map((header) => {
                        const canSort = header.column.getCanSort();
                        const isSorted = header.column.getIsSorted();

                        // Check if the header component is a React component that handles its own sorting
                        const headerContent = flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                        );

                        const column: Column<T> & ColumnWithCustomSortingLogic =
                            header.column;
                        const hasCustomSortingLogic =
                            column.columnDef.meta?.hasCustomSortingLogic ===
                            true;
                        return (
                            <HeadCell
                                key={header.id}
                                width={header.column.getSize()}
                                isSorted={isSorted}
                                onClick={
                                    canSort
                                        ? () => header.column.toggleSorting()
                                        : undefined
                                }
                                canSort={canSort}
                                hasCustomSortingLogic={hasCustomSortingLogic}
                            >
                                {headerContent}
                            </HeadCell>
                        );
                    })}
                </tr>
            ))}
        </thead>
    );
}

interface HeadCellProps {
    children: React.ReactNode;
    width?: number;
    isSorted?: SortDirection | false;
    onClick?: () => void;
    canSort?: boolean;
    hasCustomSortingLogic?: boolean;
}

function HeadCell({
    children,
    isSorted,
    onClick,
    width,
    canSort = false,
    hasCustomSortingLogic,
}: HeadCellProps) {
    return (
        <th
            onClick={onClick}
            className={cellClass({ header: true })}
            style={{ width: width ?? "auto" }}
        >
            <div className={theme.headCellContent}>
                <div className={theme.headCellLabel}>{children}</div>
                {canSort &&
                    isSorted !== undefined &&
                    !hasCustomSortingLogic && (
                        <Sorter sortDirection={isSorted} />
                    )}
            </div>
        </th>
    );
}

interface RowsProps<T extends RowData> {
    table: TSTableType<T>;
    buildRowLinkUrl?: (row: ReactTableRow<T>) => string;
}

function Rows<T extends RowData>({ table, buildRowLinkUrl }: RowsProps<T>) {
    const rowComponents = table.getRowModel().rows.map((row) => {
        const rowUrl = buildRowLinkUrl ? buildRowLinkUrl(row) : undefined;
        return <Row key={row.id} row={row} rowUrl={rowUrl} />;
    });
    return <tbody>{rowComponents}</tbody>;
}

interface RowProps<T> {
    rowUrl?: string;
    row: ReactTableRow<T>;
}

function Row<T extends RowData>({ row, rowUrl }: RowProps<T>) {
    const onClick = (event: React.MouseEvent) => {
        if (!rowUrl) {
            return;
        }

        if (event.ctrlKey || event.metaKey) {
            // Open in a new tab
            window.open(rowUrl, "_blank");
        } else {
            // Open in the same tab
            window.location.href = rowUrl;
        }
    };

    return (
        <tr className={rowClass({ link: !!rowUrl })} onClick={onClick}>
            {row.getVisibleCells().map((cell) => {
                const width = cell.column.getSize();
                return (
                    <td
                        className={cellClass({ header: false })}
                        style={{ width: width ?? "auto" }}
                        key={cell.id}
                    >
                        {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                        )}
                    </td>
                );
            })}
        </tr>
    );
}

const cellClass = cva([theme.tableCell], {
    variants: {
        header: {
            true: theme.tableHeadCell,
            false: null,
        },
    },
});

const rowClass = cva([theme.tableRow], {
    variants: {
        link: {
            true: theme.tableRowLink,
            false: null,
        },
    },
});
